import { ehNaoConformidade, type CampoChecklist, type ValorResposta } from '@qsms/shared'
import { memo, useMemo, useState } from 'react'
import { useValorEditavel } from '../dados/useValorEditavel.js'
import { Assinatura } from './Assinatura.js'
import { ImagemDoCampo } from './ImagemDoCampo.js'

/// Desenha um campo do checklist conforme o tipo. É o coração da parte dinâmica: os
/// 141 checklists da Ápia usam as mesmas oito peças, combinadas de formas diferentes.
///
/// **Cada campo guarda o próprio valor enquanto a pessoa mexe nele.** Antes, o valor
/// na tela vinha direto do IndexedDB, que só era gravado 600 ms depois da última
/// tecla. Sem estado próprio, o React desfazia cada letra no mesmo instante: nada
/// aparecia até a pausa, e numa digitação rápida só a última letra de cada rajada
/// sobrevivia. Agora a tela responde na hora, e o banco é só onde o valor descansa.
///
/// E o campo só redesenha quando o valor *dele* muda: com checklists de mais de 100
/// campos, redesenhar o formulário inteiro a cada gravação pesava no celular.

interface Props {
  campo: CampoChecklist
  valor: ValorResposta | undefined
  inspecaoId: string
  somenteLeitura?: boolean
  /// Nomes para escolher, no lugar de digitar (ex.: Gerente Contrato). Estável
  /// entre renders, como `onMudar`.
  lista?: readonly string[]
  /// Quem pode ser responsável por uma não conformidade nesta obra. Estável entre
  /// renders, como `lista`. `null`: a lista ainda não chegou a este aparelho.
  responsaveis?: readonly OpcaoResponsavel[] | null
  /// Estável entre renders — é o que permite pular os campos que não mudaram.
  onMudar: (campoId: string, valor: ValorResposta) => void
}

const VAZIO: ValorResposta = { opcoes: [] }

/// Mesmo conteúdo, sem distinguir ausente, nulo e texto vazio.
export function mesmoValor(a: ValorResposta | undefined, b: ValorResposta | undefined): boolean {
  const x = a ?? VAZIO
  const y = b ?? VAZIO
  const n = (v: unknown) => (v === undefined || v === '' ? null : v)
  return (
    n(x.texto) === n(y.texto) &&
    n(x.textoLongo) === n(y.textoLongo) &&
    n(x.numero) === n(y.numero) &&
    n(x.data) === n(y.data) &&
    n(x.booleano) === n(y.booleano) &&
    n(x.arquivoId) === n(y.arquivoId) &&
    n(x.ncResponsavel) === n(y.ncResponsavel) &&
    n(x.ncResponsavelId) === n(y.ncResponsavelId) &&
    n(x.ncAcao) === n(y.ncAcao) &&
    n(x.ncPrazo) === n(y.ncPrazo) &&
    mesmasOpcoes(x.opcoes ?? [], y.opcoes ?? [])
  )
}

function mesmasOpcoes(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((op, i) => op === b[i])
}

export const CampoResposta = memo(
  CampoRespostaBase,
  (a, b) =>
    a.campo === b.campo &&
    a.inspecaoId === b.inspecaoId &&
    a.somenteLeitura === b.somenteLeitura &&
    a.lista === b.lista &&
    a.responsaveis === b.responsaveis &&
    a.onMudar === b.onMudar &&
    mesmoValor(a.valor, b.valor),
)

function CampoRespostaBase({ campo, valor, inspecaoId, somenteLeitura, lista, responsaveis, onMudar }: Props) {
  const [local, mudarLocal] = useValorEditavel<ValorResposta>(valor ?? VAZIO, mesmoValor)

  function mudar(novo: ValorResposta) {
    mudarLocal(novo)
    onMudar(campo.id, novo)
  }

  const v = local
  const id = `campo-${campo.id}`

  return (
    <div className="cartao h-full p-4">
      <label htmlFor={id} className="rotulo">
        {campo.nome}
        {campo.obrigatorio && <span className="ml-1 text-alta">*</span>}
        {campo.itemCode && (
          <span className="ml-2 font-mono text-xs font-normal text-slate-400">{campo.itemCode}</span>
        )}
      </label>
      <Controle
        id={id}
        campo={campo}
        valor={v}
        inspecaoId={inspecaoId}
        somenteLeitura={somenteLeitura}
        lista={lista}
        onMudar={mudar}
      />
      {ehNaoConformidade(campo, v) && (
        <TratativaNaoConformidade
          id={id}
          valor={v}
          responsaveis={responsaveis === undefined ? NINGUEM : responsaveis}
          desabilitado={Boolean(somenteLeitura)}
          onMudar={mudar}
        />
      )}
    </div>
  )
}

/// Toda resposta "NC" pede quem corrige, o que será feito e até quando, e fica
/// presa a ela — é por item que depois se mede o lead time. Trocar NC por C
/// esconde o bloco sem apagar o que foi digitado: voltar para NC traz de volta. O
/// servidor só grava a tratativa da resposta que é NC.
function TratativaNaoConformidade({
  id,
  valor,
  responsaveis,
  desabilitado,
  onMudar,
}: {
  id: string
  valor: ValorResposta
  responsaveis: readonly OpcaoResponsavel[] | null
  desabilitado: boolean
  onMudar: (v: ValorResposta) => void
}) {
  return (
    <fieldset className="mt-3 space-y-2 rounded-lg border border-alta/30 bg-alta/5 p-3">
      <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-alta">Não conformidade</legend>
      <div>
        <label htmlFor={`${id}-responsavel`} className="text-xs font-medium text-slate-700">
          Responsável<span className="ml-0.5 text-alta">*</span>
        </label>
        <EscolhaResponsavel
          id={`${id}-responsavel`}
          valor={valor}
          responsaveis={responsaveis}
          desabilitado={desabilitado}
          onMudar={onMudar}
        />
      </div>
      <div>
        <label htmlFor={`${id}-acao`} className="text-xs font-medium text-slate-700">
          Ação a ser tomada<span className="ml-0.5 text-alta">*</span>
        </label>
        <textarea
          id={`${id}-acao`}
          className="campo min-h-[72px]"
          value={valor.ncAcao ?? ''}
          maxLength={2000}
          disabled={desabilitado}
          placeholder="O que será feito para corrigir"
          onChange={(e) => onMudar({ ...valor, ncAcao: e.target.value })}
        />
      </div>
      <div>
        <label htmlFor={`${id}-prazo`} className="text-xs font-medium text-slate-700">
          Prazo<span className="ml-0.5 text-alta">*</span>
        </label>
        <input
          id={`${id}-prazo`}
          type="date"
          className="campo"
          value={valor.ncPrazo ?? ''}
          min={DATA_MINIMA}
          max={DATA_MAXIMA}
          disabled={desabilitado}
          onChange={(e) => onMudar({ ...valor, ncPrazo: dataDoCampo(e.target.value) })}
        />
      </div>
    </fieldset>
  )
}

function Controle({
  id,
  campo,
  valor,
  inspecaoId,
  somenteLeitura,
  lista,
  onMudar,
}: Omit<Props, 'onMudar'> & { id: string; valor: ValorResposta; onMudar: (v: ValorResposta) => void }) {
  const desabilitado = Boolean(somenteLeitura)

  switch (campo.tipoCampo) {
    case 'TEXTO': {
      if (lista) {
        return <ListaSuspensa id={id} lista={lista} valor={valor} desabilitado={desabilitado} onMudar={onMudar} />
      }
      // Campo com opções cadastradas na verdade é uma escolha — o cadastro do
      // Dataverse tem vários assim.
      if (campo.opcoes.length > 0) {
        return (
          <SelecaoUnica id={id} campo={campo} valor={valor} desabilitado={desabilitado} onMudar={onMudar} />
        )
      }
      const longo = campo.nome.toLowerCase().includes('descri') || campo.nome.toLowerCase().includes('plano')
      return longo ? (
        <textarea
          id={id}
          className="campo min-h-[96px]"
          value={valor.textoLongo ?? valor.texto ?? ''}
          disabled={desabilitado}
          onChange={(e) => onMudar({ ...valor, textoLongo: e.target.value, texto: null })}
          placeholder="Descreva…"
        />
      ) : (
        <input
          id={id}
          className="campo"
          value={valor.texto ?? ''}
          disabled={desabilitado}
          onChange={(e) => onMudar({ ...valor, texto: e.target.value })}
        />
      )
    }

    case 'SELECAO_UNICA':
      return <SelecaoUnica id={id} campo={campo} valor={valor} desabilitado={desabilitado} onMudar={onMudar} />

    case 'MULTISSELECAO':
      return (
        <div className="space-y-2">
          {campo.opcoes.map((op) => {
            const marcado = (valor.opcoes ?? []).includes(op)
            return (
              <label
                key={op}
                className={`flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 ${
                  marcado ? 'border-marinho bg-marinho-50' : 'border-slate-200 bg-white'
                }`}
              >
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-[#003054]"
                  checked={marcado}
                  disabled={desabilitado}
                  onChange={(e) => {
                    const atuais = new Set(valor.opcoes ?? [])
                    if (e.target.checked) atuais.add(op)
                    else atuais.delete(op)
                    onMudar({ ...valor, opcoes: [...atuais] })
                  }}
                />
                <span className="text-sm text-slate-800">{op}</span>
              </label>
            )
          })}
        </div>
      )

    case 'SIM_NAO':
      return (
        <div className="flex gap-2">
          {[
            { rotulo: 'Sim', v: true, cor: 'border-leve bg-leve/10 text-leve' },
            { rotulo: 'Não', v: false, cor: 'border-alta bg-alta/10 text-alta' },
          ].map((op) => {
            const ativo = valor.booleano === op.v
            return (
              <button
                key={op.rotulo}
                type="button"
                disabled={desabilitado}
                // Segundo toque limpa: permite desmarcar uma resposta dada por engano.
                onClick={() => onMudar({ ...valor, booleano: ativo ? null : op.v })}
                className={`botao flex-1 border ${ativo ? op.cor : 'border-slate-300 bg-white text-slate-600'}`}
              >
                {op.rotulo}
              </button>
            )
          })}
        </div>
      )

    case 'NUMERO':
      return (
        <input
          id={id}
          type="number"
          inputMode="decimal"
          className="campo"
          value={valor.numero ?? ''}
          disabled={desabilitado}
          onChange={(e) =>
            onMudar({ ...valor, numero: e.target.value === '' ? null : Number(e.target.value) })
          }
        />
      )

    case 'DATA':
      return (
        <input
          id={id}
          type="date"
          className="campo"
          value={valor.data ?? ''}
          min={DATA_MINIMA}
          max={DATA_MAXIMA}
          disabled={desabilitado}
          onChange={(e) => onMudar({ ...valor, data: dataDoCampo(e.target.value) })}
        />
      )

    case 'IMAGEM':
      return (
        <ImagemDoCampo
          inspecaoId={inspecaoId}
          arquivoId={valor.arquivoId ?? null}
          somenteLeitura={desabilitado}
          onMudar={(arquivoId) => onMudar({ ...valor, arquivoId })}
        />
      )

    case 'ASSINATURA':
      return (
        <Assinatura
          inspecaoId={inspecaoId}
          arquivoId={valor.arquivoId ?? null}
          rotulo={valor.texto ?? null}
          somenteLeitura={desabilitado}
          onMudar={(arquivoId, rotulo) => onMudar({ ...valor, arquivoId, texto: rotulo })}
        />
      )
  }
}

function SelecaoUnica({
  id,
  campo,
  valor,
  desabilitado,
  onMudar,
}: {
  id: string
  campo: CampoChecklist
  valor: ValorResposta
  desabilitado: boolean
  onMudar: (v: ValorResposta) => void
}) {
  // Até 5 opções viram botões (um toque); acima disso, lista suspensa — 34
  // tipologias em botões tomariam a tela inteira.
  if (campo.opcoes.length <= 5) {
    return (
      <div className="flex flex-wrap gap-2">
        {campo.opcoes.map((op) => {
          const ativo = valor.texto === op
          return (
            <button
              key={op}
              type="button"
              disabled={desabilitado}
              onClick={() => onMudar({ ...valor, texto: ativo ? null : op })}
              className={`botao border ${
                ativo ? 'border-marinho bg-marinho text-white' : 'border-slate-300 bg-white text-slate-700'
              }`}
            >
              {op}
            </button>
          )
        })}
      </div>
    )
  }
  return (
    <select
      id={id}
      className="campo"
      value={valor.texto ?? ''}
      disabled={desabilitado}
      onChange={(e) => onMudar({ ...valor, texto: e.target.value || null })}
    >
      <option value="">Selecione…</option>
      {campo.opcoes.map((op) => (
        <option key={op} value={op}>
          {op}
        </option>
      ))}
    </select>
  )
}

/// Escolha numa lista, como a validação de dados do Excel. Grava o nome, como o
/// Power Apps.
///
/// O nome que a inspeção já tem e que não está na lista — digitado antes da lista
/// existir, ou de alguém que deixou de ser gerente — continua aparecendo e
/// selecionado. Sumir com ele apagaria a resposta sem ninguém ter pedido.
function ListaSuspensa({
  id,
  lista,
  valor,
  desabilitado,
  onMudar,
}: {
  id: string
  lista: readonly string[]
  valor: ValorResposta
  desabilitado: boolean
  onMudar: (v: ValorResposta) => void
}) {
  const atual = valor.texto ?? ''
  const foraDaLista = atual !== '' && !lista.includes(atual)
  return (
    <>
      <select
        id={id}
        className="campo"
        value={atual}
        disabled={desabilitado}
        onChange={(e) => onMudar({ ...valor, texto: e.target.value || null })}
      >
        <option value="">Selecione…</option>
        {foraDaLista && <option value={atual}>{atual}</option>}
        {lista.map((nome) => (
          <option key={nome} value={nome}>
            {nome === NAO_SE_APLICA ? 'NA (não se aplica)' : nome}
          </option>
        ))}
      </select>
      {foraDaLista && !desabilitado && (
        <p className="mt-1 text-xs text-slate-500">
          "{atual}" foi digitado antes e não está na lista. Escolha um nome da lista.
        </p>
      )}
    </>
  )
}

export interface OpcaoResponsavel {
  id: string
  nome: string
}

const NINGUEM: readonly OpcaoResponsavel[] = []
const MAX_SUGESTOES = 8
const semAcento = (t: string) =>
  t
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
const mesmoNome = (a: string, b: string) =>
  semAcento(a.trim().replace(/\s+/g, ' ')) === semAcento(b.trim().replace(/\s+/g, ' '))

/// O responsável pela NC: escolhido na lista de responsáveis da obra, com busca
/// pelo nome, ou digitado quando a pessoa não está nela — um subempreiteiro, alguém
/// recém-contratado, a lista desatualizada num aparelho sem sinal. Sem essa saída,
/// o inspetor não conseguiria finalizar.
///
/// O campo é o próprio nome: escolher na lista grava o nome e o id da pessoa;
/// digitar por cima solta o id. É o id que depois mede o tempo de atendimento de
/// cada responsável sem confundir dois nomes iguais.
function EscolhaResponsavel({
  id,
  valor,
  responsaveis,
  desabilitado,
  onMudar,
}: {
  id: string
  valor: ValorResposta
  responsaveis: readonly OpcaoResponsavel[] | null
  desabilitado: boolean
  onMudar: (v: ValorResposta) => void
}) {
  const [aberta, setAberta] = useState(false)
  // -1: nenhuma sugestão destacada. O Enter só escolhe da lista quando a pessoa
  // foi até ela; senão confirma o nome digitado.
  const [ativa, setAtiva] = useState(-1)
  const texto = valor.ncResponsavel ?? ''
  const daLista = Boolean(valor.ncResponsavelId)
  const alvo = semAcento(texto.trim())
  const baixada = responsaveis !== null
  const todos = responsaveis ?? NINGUEM
  const achadas = useMemo(
    () => (alvo ? todos.filter((r) => semAcento(r.nome).includes(alvo)) : todos),
    [todos, alvo],
  )
  // O nome digitado por inteiro, igual ao de uma pessoa da lista, é essa pessoa —
  // desde que só uma tenha esse nome. Com duas, quem digitou precisa escolher.
  const iguais = !daLista && texto.trim() ? todos.filter((r) => mesmoNome(r.nome, texto)) : []
  const sugestoes = achadas.slice(0, MAX_SUGESTOES)
  const mostrar = aberta && !desabilitado && !daLista && sugestoes.length > 0
  const listaId = `${id}-lista`

  function escolher(r: OpcaoResponsavel) {
    onMudar({ ...valor, ncResponsavel: r.nome, ncResponsavelId: r.id })
    setAberta(false)
    setAtiva(-1)
  }

  function sair() {
    setAberta(false)
    setAtiva(-1)
    if (iguais.length === 1) escolher(iguais[0]!)
  }

  return (
    <div className="relative">
      <input
        id={id}
        className="campo"
        role="combobox"
        aria-expanded={mostrar}
        aria-controls={listaId}
        aria-autocomplete="list"
        aria-activedescendant={mostrar && ativa >= 0 ? `${id}-opcao-${ativa}` : undefined}
        value={texto}
        maxLength={120}
        disabled={desabilitado}
        placeholder={todos.length > 0 ? 'Digite para buscar na lista da obra' : 'Nome de quem vai corrigir'}
        autoComplete="off"
        onFocus={() => setAberta(true)}
        onBlur={sair}
        onChange={(e) => {
          onMudar({ ...valor, ncResponsavel: e.target.value, ncResponsavelId: null })
          setAberta(true)
          setAtiva(-1)
        }}
        onKeyDown={(e) => {
          if (!mostrar) {
            if (e.key === 'ArrowDown') setAberta(true)
            return
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setAtiva((a) => Math.min(a + 1, sugestoes.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setAtiva((a) => Math.max(a - 1, -1))
          } else if (e.key === 'Enter') {
            e.preventDefault()
            const r = ativa >= 0 ? sugestoes[Math.min(ativa, sugestoes.length - 1)] : undefined
            if (r) escolher(r)
            else sair()
          } else if (e.key === 'Escape') {
            setAberta(false)
            setAtiva(-1)
          }
        }}
      />
      {mostrar && (
        // O toque na lista não pode tirar o foco do campo antes do clique chegar.
        <ul
          id={listaId}
          role="listbox"
          aria-label="Responsáveis da obra"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          onMouseDown={(e) => e.preventDefault()}
        >
          {sugestoes.map((r, i) => (
            <li
              key={r.id}
              id={`${id}-opcao-${i}`}
              role="option"
              aria-selected={i === ativa}
              onClick={() => escolher(r)}
              onMouseEnter={() => setAtiva(i)}
              className={`cursor-pointer px-3 py-2.5 text-sm ${i === ativa ? 'bg-marinho-50 text-marinho' : 'text-slate-800'}`}
            >
              {r.nome}
            </li>
          ))}
          {achadas.length > sugestoes.length && (
            <li role="presentation" className="px-3 py-2 text-xs text-slate-500">
              Mais {achadas.length - sugestoes.length}. Continue digitando o nome.
            </li>
          )}
        </ul>
      )}
      <p className="mt-1 text-xs" aria-live="polite">
        {daLista ? (
          <span className="text-leve">Da lista de responsáveis da obra.</span>
        ) : !baixada ? (
          <span className="text-slate-500">
            A lista de responsáveis ainda não chegou a este aparelho. Digite o nome.
          </span>
        ) : todos.length === 0 ? (
          <span className="text-slate-500">Nenhum responsável cadastrado nesta obra. Digite o nome.</span>
        ) : iguais.length > 1 ? (
          <span className="text-moderada">Mais de uma pessoa com esse nome: escolha na lista.</span>
        ) : texto.trim() && iguais.length === 0 ? (
          <span className="text-moderada">Não está na lista: vai ficar o nome digitado.</span>
        ) : null}
      </p>
    </div>
  )
}

/// O Chrome no computador aceita digitar um ano de até 6 dígitos num campo de
/// data ("20266-10-05"). Sem o `max` e sem conferir o formato, isso ia para o
/// servidor e recusava o envio inteiro do aparelho.
const DATA_MINIMA = '1900-01-01'
const DATA_MAXIMA = '9999-12-31'
const dataDoCampo = (v: string): string | null => (/^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null)

/// Opção que o Power Apps oferece no Gerente Contrato só para a obra 0001. Lá é um
/// pseudousuário, e é esta a grafia gravada nas respostas antigas.
export const NAO_SE_APLICA = '[Na]'
